import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { Box, Skeleton, Stack, Typography } from "@mui/material";

import { headerSty, contentSty } from "../table-styles";

import { useNavigate } from "react-router-dom";
import {
  TimesheetDetails,
  TimesheetTableProps,
} from "../../interface/timesheet";
import {
  formatDateAndTimeFromString,
  formatDateAndTimeFromString1hr,
  formatDecimal,
  formatTime,
} from "../../utils/url-id";
import { TableDots } from "./dot";
import { formatHoursToText } from "../../utils/timesheets";

export default function TimesheetTable({
  data,
  handleModal,
  loading,
}: TimesheetTableProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const headStyles = { ...headerSty, color: "#93A5D8", textAlign: "center" };
  const contentStyles = { ...contentSty, textAlign: "center", padding: "12px" };
  const [openRow, setOpenRow] = React.useState<number | null>(null); // Track which row is open

  const [open, setOpen] = React.useState(false);

  const handleToggleOpen = (rowId: number) => {
    setOpenRow(openRow === rowId ? null : rowId);
  };

  const handleStatusChange = (newStatus: string) => {
    // Optimistic update
    // const updatedStatuses = [...statuses];
    // updatedStatuses[rowId] = newStatus; // Update local state immediately
    // setStatuses(updatedStatuses);
    // Simulate a server request
    // setTimeout(() => {
    // Here you would typically call your update API
    // For example: api.updateStatus(rowId, newStatus).then(...).catch(...);
    // If the API call fails, you can revert the optimistic change if needed
    // In this case, we are simulating a successful update without reverting.
    // }, 1000); // Simulate network delay
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        background: theme.palette.common.white,
        borderRadius: "16px",
        boxShadow: "none",
        minHeight: 400,
      }}
    >
      <Table
        sx={{
          minWidth: 650,
          boxShadow: "none",

          paddingRight: 2,
        }}
        aria-label="simple table"
      >
        <TableHead sx={{ paddingX: 2 }}>
          <TableRow>
            <TableCell sx={{ ...headStyles, width: "13%" }}>
              Employee Name
            </TableCell>
            <TableCell sx={{ ...headStyles }}>Shift Start Date</TableCell>
            <TableCell sx={{ ...headStyles }}>Shift End Date</TableCell>
            <TableCell sx={{ ...headStyles }}>Clock-in</TableCell>
            <TableCell sx={{ ...headStyles }}>Clock-out</TableCell>
            <TableCell sx={{ ...headStyles }}>Total Hours</TableCell>
            <TableCell sx={{ ...headStyles }}>Overtime Hours</TableCell>

            <TableCell sx={{ ...headStyles }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading &&
            data.map((row: TimesheetDetails, index: number) => (
              <TableRow key={row?._id}>
                <TableCell
                  sx={{ ...contentStyles }}
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                >
                  {row?.medic_info?.name}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatDateAndTimeFromString(row?.shift_info?.start_time) ??
                    "-"}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatDateAndTimeFromString(row?.shift_info?.end_time) ??
                    "-"}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatDateAndTimeFromString1hr(row?.clock_in_time) ?? "-"}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatDateAndTimeFromString1hr(row.clock_out_time) ?? "-"}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatHoursToText(row.total_hours ?? "-")}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  {formatHoursToText(row.overtime_hours) ?? "-"}
                </TableCell>
                <TableCell
                  onClick={() => navigate(`/timesheet/${row?._id}`)}
                  sx={{ ...contentStyles }}
                >
                  <TmesheetEmpStatus status={row?.status} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {data?.length === 0 && !loading && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Timesheet Available.
          </Typography>
        </Box>
      )}
      {loading && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
  );
}

export const TmesheetEmpStatus: React.FC<{
  status: "pending" | "approved" | "decline" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "pending":
        return {
          color: theme.palette.warning.main,
          backgroundColor: "#ffac336b",
        };
      case "decline":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "approved":
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.light,
        };
      default:
        return {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.common.white,
        };
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Clocked In";

      case "decline":
        return "Declined";

      case "approved":
        return "Clocked Out";

      default:
        return status;
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 120, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",

          ...statusStyles,
        }}
      >
        {getStatusText(status)}
      </Box>
    </Stack>
  );
};
